import { useCallback, useMemo } from "react";

import {
  InfiniteData,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { caseQueuesApi } from "src/api/endpoints";
import { deserializeJsonApi } from "src/api/jsonApiHelpers";
import { useCurrentWorkspace } from "src/api/queries";
import { DropdownSelection } from "src/cases/common/CaseTemplateDropdown";
import {
  CaseQueueSummary,
  DeserializedCaseQueue,
  DeserializedQueueCase,
  JsonApiRelationshipData,
} from "src/cases/queues/types";
import {
  buildDefaultTemplateVersionInclude,
  createQueueAtomicOperation,
  createQueueAtomicOperationIncludes,
  DEFAULT_TEMPLATE_VERSION_RESOURCE_ID,
} from "src/cases/queues/utils";
import {
  CasesResponse,
  JsonApiAtomicResult,
  JsonApiResourceObjectUnionQueueMemberAttributesQueueTemplateConfigAttributesQueueDefaultTemplateVersionAttributesAny,
  QueueAtomicOperationsRequest,
  QueueCreateAttributes,
  QueueCreateResponse,
  QueuesResponse,
} from "src/clients/case-management-service";
import { Environment } from "src/hooks/useEnvironment";
import { DashboardPageParams } from "src/router/urls";
import { encodeUrlParams } from "src/utils/url";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const CASE_QUEUES_QUERY_KEY_PREFIX = "caseQueues";

export const caseQueuesKeys = {
  all: (workspaceId: string) =>
    [CASE_QUEUES_QUERY_KEY_PREFIX, workspaceId] as const,
  get: (workspaceId: string, queueId?: string) =>
    [...caseQueuesKeys.all(workspaceId), "queue", queueId] as const,
  summary: (workpaceId: string, queueId?: string, environment?: Environment) =>
    [
      ...caseQueuesKeys.get(workpaceId, queueId),
      "summary",
      environment,
    ] as const,
  cases: (
    workpaceId: string,
    queueId?: string,
    environment?: Environment,
    filters?: Record<string, string>,
    sort?: string,
  ) =>
    [
      ...caseQueuesKeys.get(workpaceId, queueId),
      "cases",
      environment,
      filters,
      sort,
    ] as const,
  siblingCase: (
    workspaceId: string,
    queueId?: string,
    environment?: Environment,
    currentCaseId?: string,
    direction?: "prev" | "next",
    filters?: Record<string, string>,
  ) =>
    [
      ...caseQueuesKeys.get(workspaceId, queueId),
      "siblingCase",
      environment,
      currentCaseId,
      direction,
      filters,
    ] as const,
};

const useApiClient = () => {
  const { data: workspace } = useCurrentWorkspace();
  return useMemo(
    () => (workspace?.base_url ? caseQueuesApi(workspace.base_url) : undefined),
    [workspace?.base_url],
  );
};

export type CreateCaseQueueData = {
  templateIds: string[];
  memberIds: string[];
  attributes: QueueCreateAttributes;
  defaultTemplateVersion?: DropdownSelection | null;
};

type CreateCaseQueueInclude =
  JsonApiResourceObjectUnionQueueMemberAttributesQueueTemplateConfigAttributesQueueDefaultTemplateVersionAttributesAny;

export const useCreateCaseQueue = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<QueueCreateResponse, AxiosError, CreateCaseQueueData>({
    mutationFn: async ({
      attributes,
      templateIds,
      memberIds,
      defaultTemplateVersion,
    }) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const response = await apiClient.createQueueEndpointApiV1QueuesPost({
        queueCreateRequest: {
          data: {
            type: "queues",
            attributes,
            relationships: {
              members: {
                data: memberIds.map((id) => ({
                  type: "queue_members",
                  id,
                })) as unknown as JsonApiRelationshipData,
              },
              template_configs: {
                data: templateIds.map((id) => ({
                  type: "template_configs",
                  id,
                })) as unknown as JsonApiRelationshipData,
              },
              default_template_version: defaultTemplateVersion
                ? {
                    data: {
                      type: "default_template_versions",
                      id: DEFAULT_TEMPLATE_VERSION_RESOURCE_ID,
                    } as unknown as JsonApiRelationshipData,
                  }
                : null,
            },
          },
          included: [
            ...memberIds.map(
              (id) =>
                ({
                  type: "queue_members",
                  id,
                  attributes: { type: "user" },
                  relationships: {
                    user: {
                      data: { type: "users", id },
                    },
                  },
                }) as unknown as CreateCaseQueueInclude,
            ),
            ...templateIds.map(
              (id) =>
                ({
                  type: "template_configs",
                  id,
                  attributes: { assignment_logic: "self-assign" },
                  relationships: {
                    template: {
                      data: { type: "templates", id },
                    },
                  },
                }) as unknown as CreateCaseQueueInclude,
            ),
            ...(defaultTemplateVersion
              ? [buildDefaultTemplateVersionInclude(defaultTemplateVersion)]
              : []),
          ],
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: caseQueuesKeys.all(wsId),
      });
    },
  });
};

type CaseQuerySummaryParams = {
  queueId?: string;
  environment: Environment;
  userId?: string;
};

const CASES_REFETCH_INTERVAL = 2 * 60 * 1000; // 2 minutes

export const useCaseQueueSummary = (params: CaseQuerySummaryParams) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useApiClient();
  const { userId, queueId, environment } = params;

  return useQuery<CaseQueueSummary, AxiosError>({
    queryKey: caseQueuesKeys.summary(wsId, queueId, environment),
    queryFn: async () => {
      if (!apiClient || !queueId || !userId) {
        throw new Error("API client and queue or user ID is missing");
      }

      const response =
        await apiClient?.getQueueSummaryEndpointApiV1QueuesQueueIdSummaryGet({
          queueId,
          filterEnv: environment,
          filterUserId: userId,
        });

      return response?.data.data;
    },
    staleTime: 0,
    refetchOnMount: true,
    refetchInterval: CASES_REFETCH_INTERVAL,
    enabled: !!queueId && !!apiClient && !!userId,
  });
};

const CASE_QUEUES_STALE_TIME = 5 * 60 * 1000; // 5 minutes

const deserializeCaseQueues = (data: QueuesResponse): DeserializedCaseQueue[] =>
  deserializeJsonApi<DeserializedCaseQueue[]>({
    data: data?.data ?? [],
    included: data?.included ?? [],
  }).toSorted((a, b) => a.name.localeCompare(b.name));

export const useCaseQueues = <TData = DeserializedCaseQueue[]>(
  options: Omit<
    UseQueryOptions<QueuesResponse, AxiosError, TData>,
    "queryKey" | "queryFn"
  > = { enabled: true },
) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useApiClient();

  return useQuery<QueuesResponse, AxiosError, TData>({
    queryKey: caseQueuesKeys.all(wsId),
    queryFn: async () => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const response = await apiClient.getQueuesApiV1QueuesGet();
      return response.data;
    },
    staleTime: CASE_QUEUES_STALE_TIME,
    select: deserializeCaseQueues as (data: QueuesResponse) => TData,
    ...options,
    enabled: !!apiClient && !!options?.enabled,
  });
};

export type CaseQueue = {
  data?: QueuesResponse["data"][number];
  included?: QueuesResponse["included"];
};

const selectDeserializedCaseQueue = (
  data: QueuesResponse,
  queueId?: string,
) => {
  return deserializeJsonApi<DeserializedCaseQueue>({
    data: data.data.find((queue) => queue.id === queueId),
    included: data.included,
  });
};

export const useCaseQueue = (queueId?: string) => {
  const selectQueue = useCallback(
    (data: QueuesResponse) => selectDeserializedCaseQueue(data, queueId),
    [queueId],
  );

  return useCaseQueues({
    select: selectQueue,
    enabled: !!queueId,
  });
};

export type UpdateCaseQueueData = CreateCaseQueueData & {
  attributes: Partial<CreateCaseQueueData["attributes"]>;
  queueId: string;
};

export const useUpdateCaseQueue = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<JsonApiAtomicResult, AxiosError, UpdateCaseQueueData>({
    mutationFn: async (params) => {
      if (!apiClient) {
        throw new Error("API client is missing");
      }

      const queueAtomicOperationsRequest: QueueAtomicOperationsRequest = {
        "atomic:operations": createQueueAtomicOperation(params),
        included: createQueueAtomicOperationIncludes({
          memberIds: params.memberIds,
          templateIds: params.templateIds,
          defaultTemplateVersion: params.defaultTemplateVersion,
        }),
      };

      const response =
        await apiClient.applyQueueAtomicOperationsApiV1QueuesQueueIdOperationsPost(
          {
            queueId: params.queueId,
            queueAtomicOperationsRequest,
          },
        );

      return response;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: caseQueuesKeys.get(wsId, variables.queueId),
      });
      await queryClient.invalidateQueries({
        queryKey: caseQueuesKeys.all(wsId),
      });
    },
  });
};

type ListQueueCasesParams = {
  queueId?: string;
  environment: Environment;
  filters?: Record<string, string>;
  sort?: string;
  enabled?: boolean;
};

const CASES_PAGE_SIZE = 50;
export const CASES_DEFAULT_SORT = "-created_at";

const deserializeQueueCases = (
  params: Pick<CasesResponse, "data" | "included">,
) => deserializeJsonApi<DeserializedQueueCase[]>(params);

export const useQueueCases = (params: ListQueueCasesParams) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useApiClient();
  const { queueId, environment, filters, sort = CASES_DEFAULT_SORT } = params;

  const selectQueueCases = useCallback((data: InfiniteData<CasesResponse>) => {
    const cases = data.pages.flatMap((page) => page.data);
    const included = data.pages.flatMap((page) => page.included ?? []);

    return deserializeQueueCases({
      data: cases,
      included,
    });
  }, []);

  return useInfiniteQuery<CasesResponse, AxiosError, DeserializedQueueCase[]>({
    queryKey: caseQueuesKeys.cases(wsId, queueId, environment, filters, sort),
    queryFn: async ({ pageParam, signal }) => {
      if (!apiClient || !queueId) {
        throw new Error("API client or queue ID is missing");
      }

      const {
        ["filter[assignee_id][eq]"]: filterAssigneeId,
        ["filter[createdAt][gte]"]: filterCreatedAtGte,
        ["filter[createdAt][lte]"]: filterCreatedAtLte,
        ...restFilters
      } = filters || {};

      const response =
        await apiClient.listCasesEndpointV2ApiV2QueuesQueueIdCasesGet(
          {
            queueId: queueId,
            sort,
            filterEnv: environment,
            pageSize: CASES_PAGE_SIZE,
            pageCursor: pageParam as string,
            filterAssigneeId,
            filterCreatedAtGte,
            filterCreatedAtLte,
          },
          {
            signal,
            params: encodeUrlParams(restFilters),
          },
        );

      return response.data;
    },
    enabled: !!apiClient && !!queueId && (params.enabled ?? true),
    initialPageParam: undefined,
    refetchOnMount: true,
    refetchInterval: CASES_REFETCH_INTERVAL,
    select: selectQueueCases,
    getNextPageParam: (lastPage) => {
      if (lastPage.links.next) {
        const url = new URL(lastPage.links.next);
        const search = new URLSearchParams(url.search);
        const cursor = search.get("page[cursor]");
        return cursor;
      }
    },
  });
};

type UseSiblingCasesParams = {
  currentCaseId: string;
  queueId: string | undefined;
  environment: Environment;
  filters?: Record<string, string>;
  enabled?: boolean;
};

export const useSiblingCases = (params: UseSiblingCasesParams) => {
  const { currentCaseId, queueId, environment, filters } = params;
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useApiClient();

  const enabled =
    !!apiClient && !!queueId && !!currentCaseId && (params.enabled ?? true);

  const fetchSibling = useCallback(
    async (
      direction: "prev" | "next",
      signal?: AbortSignal,
    ): Promise<string | null> => {
      if (!apiClient || !queueId) return null;

      const reverseSort = CASES_DEFAULT_SORT.startsWith("-")
        ? CASES_DEFAULT_SORT.slice(1)
        : `-${CASES_DEFAULT_SORT}`;
      const sort = direction === "next" ? CASES_DEFAULT_SORT : reverseSort;

      const {
        ["filter[assignee_id][eq]"]: filterAssigneeId,
        ["filter[createdAt][gte]"]: filterCreatedAtGte,
        ["filter[createdAt][lte]"]: filterCreatedAtLte,
        ...restFilters
      } = filters || {};

      const response =
        await apiClient.listCasesEndpointV2ApiV2QueuesQueueIdCasesGet(
          {
            queueId,
            sort,
            filterEnv: environment,
            pageSize: 1,
            pageCursor: currentCaseId,
            filterAssigneeId,
            filterCreatedAtGte,
            filterCreatedAtLte,
          },
          {
            signal,
            params: encodeUrlParams(restFilters),
          },
        );

      const cases = response.data.data;

      return cases?.[0]?.id ?? null;
    },
    [apiClient, queueId, environment, currentCaseId, filters],
  );

  const prevQuery = useQuery<string | null, AxiosError>({
    queryKey: caseQueuesKeys.siblingCase(
      wsId,
      queueId,
      environment,
      currentCaseId,
      "prev",
      filters,
    ),
    queryFn: ({ signal }) => fetchSibling("prev", signal),
    enabled,
  });

  const nextQuery = useQuery<string | null, AxiosError>({
    queryKey: caseQueuesKeys.siblingCase(
      wsId,
      queueId,
      environment,
      currentCaseId,
      "next",
      filters,
    ),
    queryFn: ({ signal }) => fetchSibling("next", signal),
    enabled,
  });

  return {
    prevCaseId: prevQuery.data ?? null,
    nextCaseId: nextQuery.data ?? null,
  };
};
